import React, { useState } from 'react';
import type { QuestionConfig, SurveyMode } from '../types';
import { SurveyHubHero } from './SurveyHubHero';
import '../survey-hub.css';

interface Props {
  title: string;
  description: string;
  audience: string;
  language: string;
  mode: SurveyMode;
  questions: QuestionConfig[];
  onBackToEdit: () => void;
  onApproveAndPublish: () => void;
}

/** Exact respondent preview — template preview() + respondentForm(). */
export const RespondentPreviewModal: React.FC<Props> = ({
  title,
  mode,
  questions,
  onBackToEdit,
  onApproveAndPublish,
}) => {
  const [busy, setBusy] = useState(false);

  return (
    <div className="survey-hub-content">
      <SurveyHubHero
        title="Предпросмотр респондента"
        description="Точный вид участника. До публикации можно вернуться и изменить форму."
      />

      <div className="survey-hub-preview-shell">
        <div className="survey-hub-preview-brand">
          <small>Zertteu Academy · исследовательский опрос</small>
          <h2>{title}</h2>
          <p style={{ margin: '7px 0 0', opacity: 0.76 }}>
            {mode === 'VERIFIED'
              ? 'Ответ с подтверждением · e-mail подтверждён'
              : 'Анонимный ответ · e-mail не требуется'}
          </p>
        </div>
        <form
          className="survey-hub-preview-body"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <p>
            Ответы используются только в рамках указанного исследования. Кабинет и ролевое меню на
            гостевой странице недоступны.
          </p>
          {questions.map((q, i) => {
            const type = q.question_type;
            let input: React.ReactNode = null;
            if (type === 'text') {
              input = <textarea name={`q${i}`} rows={3} />;
            } else if (type === 'date') {
              input = <input type="date" name={`q${i}`} />;
            } else if (type === 'scale') {
              input = (
                <div className="survey-hub-radio">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <span key={v}>
                      <input type="radio" name={`q${i}`} value={v} /> {v}
                    </span>
                  ))}
                </div>
              );
            } else if (type === 'multi') {
              const opts = q.options?.length ? q.options : ['Вариант A', 'Вариант B', 'Вариант C'];
              input = (
                <div className="survey-hub-radio">
                  {opts.map((v) => (
                    <span key={v}>
                      <input type="checkbox" name={`q${i}`} value={v} /> {v}
                    </span>
                  ))}
                </div>
              );
            } else if (type === 'matrix') {
              input = (
                <div style={{ fontSize: 12, color: '#5d6c78' }}>
                  Матрица: {(q.matrix_rows || ['Публикации', 'Стажировка', 'Практика']).join(' / ')} ×
                  1–5
                </div>
              );
            } else {
              const opts = q.options?.length ? q.options : ['Да', 'Нет'];
              input = (
                <div className="survey-hub-radio">
                  {opts.map((v) => (
                    <span key={v}>
                      <input type="radio" name={`q${i}`} value={v} /> {v}
                    </span>
                  ))}
                </div>
              );
            }
            return (
              <div key={i} className="survey-hub-rq">
                <label>
                  {i + 1}. {q.title}
                </label>
                {input}
              </div>
            );
          })}
          <button className="survey-hub-btn primary" type="submit" style={{ marginTop: 18 }} disabled>
            Отправить ответы
          </button>
        </form>
      </div>

      <div className="survey-hub-actions" style={{ justifyContent: 'center' }}>
        <button type="button" className="survey-hub-btn" onClick={onBackToEdit}>
          ← Редактировать
        </button>
        <button
          type="button"
          className="survey-hub-btn gold"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onApproveAndPublish();
            } finally {
              setBusy(false);
            }
          }}
        >
          Утвердить структуру →
        </button>
      </div>
    </div>
  );
};
